import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useSEO } from "@/hooks/useSEO";
import { getLocalizedRegionSEO } from "@/lib/seoUtils";
import { MapPin, Search, Route, Share2, Store, Crown, Home, Zap, Heart, Sparkles, Palette, Users } from "lucide-react";
import { SchemaMarkup } from "@/components/SchemaMarkup";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Footer } from "@/components/Footer";
import { RouteCard } from "@/components/RouteCard";
import { PostCard } from "@/components/PostCard";
import { featuredRoute } from "@/lib/dummyData";
import type { Post } from "@shared/schema";
import { useRestyleVisibility } from "@/lib/featureFlags";
import { useAuth } from "@/lib/authContext";
import { useI18n } from "@/lib/i18n";
import { getPreferredGeo } from "@/lib/geoPreferences";

// Kringloopwinkel foto's voor hero sectie (wisselt per pagina load)
const heroImages = [
  "/1.jpg",
  "/2.jpg",
  "/3.jpg",
  "/4.jpg",
  "/5.jpg",
  "/6.jpg",
];

export default function HomePage() {
  const { user } = useAuth();
  const { locale, t } = useI18n();
  const preferredGeo = getPreferredGeo(user, locale);

  const regionSEO = getLocalizedRegionSEO({
    regionName: (preferredGeo.stateOrProvince || preferredGeo.country) as string,
    countryCode: preferredGeo.country || undefined,
    stateOrProvince: preferredGeo.stateOrProvince || undefined
  }, locale);

  useSEO({
    titleKey: "seo.home.title",
    descriptionKey: "seo.home.description",
    dynamicValues: { region: regionSEO },
    isRawTitle: true
  });

  const [leftImage, setLeftImage] = useState<string | null>(null);
  const [rightImage, setRightImage] = useState<string | null>(null);
  const [weeklyWinners, setWeeklyWinners] = useState<{
    postWinner: Post | null;
    routeWinner: any | null;
  }>({ postWinner: null, routeWinner: null });
  const { canSeeRestyle } = useRestyleVisibility();

  const proFeatures = [
    { icon: Route, text: t("home.hero.routes") },
    { icon: Users, text: t("upgrade.perk.socialWishlist") },
    ...(canSeeRestyle ? [{ icon: Palette, text: t("nav.restyle") }] : []),
    { icon: Zap, text: t("home.hero.optimize") || "Route Optimalisatie" },
    { icon: Home, text: t("home.hero.startHome") || "Start & eindig thuis" },
    { icon: Share2, text: t("home.hero.statusInsider") || "Status: VintMap Insider" }
  ];

  useEffect(() => {
    const shuffled = [...heroImages].sort(() => Math.random() - 0.5);
    setLeftImage(shuffled[0] || null);
    setRightImage(shuffled[1] || shuffled[0] || null);

    async function loadWeeklyWinners() {
      try {
        const params = new URLSearchParams();
        if (preferredGeo.country) params.set("country", preferredGeo.country);
        if (preferredGeo.stateOrProvince) params.set("region", preferredGeo.stateOrProvince);
        const response = await fetch(`/api/weekly-winners?${params.toString()}`);
        if (response.ok) {
          const data = await response.json();
          setWeeklyWinners({
            postWinner: data.postWinner || null,
            routeWinner: data.routeWinner || null,
          });
        }
      } catch (error) {
        console.error("Error loading weekly winners:", error);
      }
    }
    loadWeeklyWinners();
  }, [preferredGeo.country, preferredGeo.stateOrProvince]);

  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "VintMap",
    "description": t("seo.home.description", { region: regionSEO }),
    "url": locale === 'nl' ? "https://vintmap.nl" : "https://www.vintmap.app",
    "applicationCategory": "TravelApplication",
    "offers": {
      "@type": "Offer",
      "price": "2.99",
      "priceCurrency": "EUR"
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SchemaMarkup data={webAppSchema} />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-primary py-12 md:py-24 min-h-[80vh] md:min-h-[600px] flex items-center">
          {leftImage && (
            <div className="hidden lg:block absolute left-0 top-0 bottom-0 w-1/4 overflow-hidden">
              <img
                src={leftImage}
                alt={t("home.hero.imageAlt")}
                className="w-full h-full object-cover opacity-90"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          )}

          {rightImage && (
            <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-1/4 overflow-hidden">
              <img
                src={rightImage}
                alt={t("home.hero.imageAlt")}
                className="w-full h-full object-cover opacity-90"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          )}

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl lg:max-w-[48%] mx-auto text-center space-y-6 px-4 sm:px-8">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-black leading-tight">
                {t("home.hero.title")}
              </h1>

              <p className="text-base sm:text-lg text-white/90 max-w-xl mx-auto">
                {t("home.hero.subtitle")}
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
                <Link href="/routes">
                  <Button size="lg" data-testid="button-discover-routes" className="bg-white text-primary hover:bg-white/90 w-full sm:w-auto">
                    <MapPin className="h-5 w-5 mr-2" />
                    {t("home.hero.routes")}
                  </Button>
                </Link>
                <Link href="/shops">
                  <Button size="lg" variant="outline" data-testid="button-find-shops" className="bg-black text-white border-black hover:bg-black/90 w-full sm:w-auto">
                    <Store className="h-5 w-5 mr-2" />
                    {t("home.hero.shops")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-16 bg-card">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-4 lg:mb-8">
                  <div>
                    <Badge variant="secondary" className="mb-2">{t("home.featured")}</Badge>
                    <h2 className="text-xl lg:text-2xl font-bold flex items-center gap-2">
                      {t("home.gem.title")}
                      <Heart className="h-5 w-5 lg:h-6 lg:w-6 text-magenta fill-magenta" />
                    </h2>
                  </div>
                  <Link href="/community">
                    <Button variant="ghost" size="sm" className="hidden sm:inline-flex">{t("home.posts.all")}</Button>
                  </Link>
                </div>

                {weeklyWinners.postWinner && weeklyWinners.postWinner.id ? (
                  <div className="w-full flex-1">
                    <PostCard post={weeklyWinners.postWinner} />
                  </div>
                ) : (
                  <Card className="w-full flex-1 border-0 bg-muted/30">
                    <CardContent className="p-6 text-center h-full flex flex-col items-center justify-center">
                      <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">
                        {t("home.gem.empty")}
                      </p>
                    </CardContent>
                  </Card>
                )}
                <div className="mt-2 sm:hidden text-center">
                  <Link href="/community">
                    <Button variant="link" size="sm">{t("home.posts.viewAll")}</Button>
                  </Link>
                </div>
              </div>

              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-4 lg:mb-8">
                  <div>
                    <Badge variant="secondary" className="mb-2">{t("home.featured")}</Badge>
                    <h2 className="text-xl lg:text-2xl font-bold flex items-center gap-2">
                      {t("home.route.title")}
                      <Route className="h-5 w-5 lg:h-6 lg:w-6 text-primary" />
                    </h2>
                  </div>
                  <Link href="/routes">
                    <Button variant="ghost" size="sm" className="hidden sm:inline-flex">{t("home.routes.all")}</Button>
                  </Link>
                </div>

                {weeklyWinners.routeWinner && weeklyWinners.routeWinner.id ? (
                  <div className="w-full flex-1">
                    <RouteCard route={weeklyWinners.routeWinner} />
                  </div>
                ) : (
                  <div className="w-full flex-1">
                    {featuredRoute && featuredRoute.id && <RouteCard route={featuredRoute} />}
                  </div>
                )}
                <div className="mt-2 sm:hidden text-center">
                  <Link href="/routes">
                    <Button variant="link" size="sm">{t("home.routes.viewAll")}</Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">{t("home.how.title")}</h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                {t("home.how.subtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[{
                icon: Search, title: t("home.how.step1.title"), description: t("home.how.step1.desc")
              }, {
                icon: Route, title: t("home.how.step2.title"), description: t("home.how.step2.desc")
              }, {
                icon: Share2, title: t("home.how.step3.title"), description: t("home.how.step3.desc")
              }].map(({ icon: Icon, title, description }) => (
                <Card key={title} className="text-center border-0 bg-transparent">
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{title}</h3>
                    <p className="text-sm text-muted-foreground">{description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 md:py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center max-w-6xl mx-auto">
              <div className="hidden lg:block relative rounded-2xl overflow-hidden shadow-xl aspect-square">
                <img
                  src="/insider.png"
                  alt="VintMap Insider"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-8">
                  <p className="text-white font-medium italic">{t("home.insider.quote")}</p>
                </div>
              </div>

              <div className="text-left">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Crown className="h-6 w-6 text-primary" />
                  </div>
                  <Badge variant="secondary" className="text-sm px-3 py-1">{t("home.insider.badge")}</Badge>
                </div>

                <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
                  {t("home.insider.titlePrefix") || "VintMap"} <span className="text-primary">{t("home.insider.titleHighlight") || "Insider"}</span>
                </h2>
                <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                  {t("home.insider.subtitle")}
                </p>

                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
                  {proFeatures.map(({ icon: Icon, text }) => (
                    <div key={text} className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border/50 shadow-sm">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-xs font-semibold">{text}</span>
                    </div>
                  ))}
                </div>

                <Card className="bg-primary/5 border-primary/20 mb-8">
                  <CardContent className="py-4 px-6 flex items-start gap-4">
                    <Home className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold mb-1">{t("home.insider.doorToDoor.title")}</p>
                      <p className="text-sm text-muted-foreground">
                        {t("home.insider.doorToDoor.desc")}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <Link href="/account?tab=settings" className="w-full sm:w-auto">
                    <Button size="lg" data-testid="button-get-pro" className="w-full px-8 h-12 text-base bg-magenta hover:bg-magenta/90 text-white font-bold">
                      {t("home.insider.cta")}
                    </Button>
                  </Link>
                  <div className="text-left">
                    <p className="text-2xl font-bold text-primary leading-none">
                      €2,99
                      <span className="text-sm font-normal text-muted-foreground ml-1">{t("home.insider.priceMonth")}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("home.insider.priceYear")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
